import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dog, Cat, Scissors, Droplets, Ear, Wind } from "lucide-react";

const dogServices = [
  { icon: Droplets, name: "Bath & Blow Dry", description: "Thorough cleansing with premium shampoos" },
  { icon: Scissors, name: "Hair Trimming", description: "Breed-specific cuts and styling" },
  { icon: "nail", name: "Nail Clipping", description: "Safe and gentle nail care" },
  { icon: Ear, name: "Ear Cleaning", description: "Careful ear hygiene maintenance" },
  { icon: Wind, name: "De-shedding", description: "Reduce shedding with specialized tools" },
];

const catServices = [
  { icon: Droplets, name: "Gentle Bath", description: "Stress-free bathing for felines" },
  { icon: "brush", name: "Coat Brushing", description: "Detangling and coat maintenance" },
  { icon: "nail", name: "Nail Trim", description: "Quick and gentle nail trimming" },
  { icon: Scissors, name: "Mat Removal", description: "Careful removal of matted fur" },
];

export const ServicesSection = () => {
  const renderIcon = (IconComponent) => {
    if (IconComponent === "nail") {
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 12h12M6 12l4 8M6 12l4-8M18 12l-4 8M18 12l-4-8" />
        </svg>
      );
    }
    if (IconComponent === "brush") {
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 21h4l13-13a2 2 0 000-3l-1-1a2 2 0 00-3 0L3 17v4z" />
          <path d="M14.5 5.5l4 4" />
        </svg>
      );
    }
    return <IconComponent className="w-5 h-5" />;
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-16 md:py-24 bg-slate-50"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Our Grooming Services
          </h2>
          <div className="section-divider mx-auto mb-6"></div>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Comprehensive grooming services tailored for your pet's comfort and style
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Dog Grooming */}
          <div className="space-y-6">
            <Card
              data-testid="dog-services-card"
              className="service-card border-0 shadow-lg overflow-hidden"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/19145901/pexels-photo-19145901.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Professional dog grooming"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                    <Dog className="w-6 h-6 text-sky-600" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-white">
                    Dog Grooming
                  </h3>
                </div>
              </div>
              <CardContent className="p-6">
                <ul className="space-y-4">
                  {dogServices.map((service, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-4 p-3 rounded-xl hover:bg-sky-50 transition-colors"
                    >
                      <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center text-sky-600 shrink-0">
                        {renderIcon(service.icon)}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{service.name}</p>
                        <p className="text-sm text-slate-500">{service.description}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Cat Grooming */}
          <div className="space-y-6">
            <Card
              data-testid="cat-services-card"
              className="service-card border-0 shadow-lg overflow-hidden"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/6130979/pexels-photo-6130979.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Professional cat grooming"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                    <Cat className="w-6 h-6 text-amber-500" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-white">
                    Cat Grooming
                  </h3>
                </div>
              </div>
              <CardContent className="p-6">
                <ul className="space-y-4">
                  {catServices.map((service, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-4 p-3 rounded-xl hover:bg-amber-50 transition-colors"
                    >
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center text-amber-600 shrink-0">
                        {renderIcon(service.icon)}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{service.name}</p>
                        <p className="text-sm text-slate-500">{service.description}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Pricing Note */}
        <div
          data-testid="pricing-note"
          className="mt-12 text-center p-6 bg-white rounded-2xl border border-slate-200 shadow-sm"
        >
          <p className="text-slate-600">
            <span className="font-semibold text-slate-900">Transparent Pricing:</span>{" "}
            Pricing varies based on breed, size, and coat condition. A transparent quote is provided before service.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
